import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, RefreshCw, ArrowRight, ShieldCheck } from "lucide-react";

// Batch Draft Management — drafts live ONLY in `payroll_drafts`; deleting here
// never touches payroll runs, pay stubs or approved timesheets.
export default function ManageDrafts() {
  const [drafts, setDrafts] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmIds, setConfirmIds] = useState(null); // array of ids pending confirmation
  const [deleteErr, setDeleteErr] = useState("");
  const selectAllRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/payroll/drafts");
      setDrafts(data);
      setSel((prev) => prev.filter((id) => data.some((d) => d.id === id)));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allSelected = drafts.length > 0 && sel.length === drafts.length;
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = sel.length > 0 && !allSelected;
  }, [sel, allSelected]);

  const doDelete = async () => {
    const ids = confirmIds || [];
    setBusy(true);
    setDeleteErr("");
    try {
      const { data } = await api.post("/payroll/drafts/bulk-delete", { ids });
      const skipped = (data.requested || ids.length) - (data.deleted || 0);
      toast.success(`${data.deleted} draft${data.deleted === 1 ? "" : "s"} permanently deleted`, {
        description: skipped > 0
          ? `${skipped} draft(s) were already gone. Payroll history and approved timesheets were untouched.`
          : "Payroll history, released stubs and approved timesheets were untouched.",
      });
      setSel([]);
      setConfirmIds(null);
      await load();
    } catch (e) {
      const msg = formatApiError(e);
      setDeleteErr(msg);
      toast.error(msg);
    }
    setBusy(false);
  };

  const isAutosave = (d) => (d.name || "").startsWith("⟳ autosave");

  return (
    <div data-testid="manage-drafts-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="font-mono text-xs text-muted-foreground max-w-xl leading-relaxed">
          In-progress runs only. Nothing here has been disbursed — deleting a draft removes just the draft record.
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="drafts-refresh-btn" className="border-2 border-ink px-3 py-2 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2 hover:bg-ink hover:text-white">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            data-testid="delete-selected-drafts-btn"
            disabled={sel.length === 0}
            onClick={() => setConfirmIds(sel)}
            className="border-2 border-bad bg-bad text-white px-4 py-2 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete Selected ({sel.length})
          </button>
        </div>
      </div>

      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="drafts-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="px-4 py-3 w-10 text-left">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  data-testid="drafts-select-all"
                  checked={allSelected}
                  onChange={() => setSel(allSelected ? [] : drafts.map((d) => d.id))}
                  className="w-4 h-4 accent-white"
                />
              </th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Draft</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Period</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Pay Date</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Lines</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Updated</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {drafts.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="drafts-empty">No payroll drafts in progress.</td></tr>
            )}
            {drafts.map((d, i) => (
              <tr key={d.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`draft-manage-row-${d.id}`}>
                <td className="px-4 py-3 border-t border-ink/20">
                  <input type="checkbox" data-testid={`draft-select-${d.id}`} checked={sel.includes(d.id)} onChange={() => toggle(d.id)} className="w-4 h-4 accent-ink" />
                </td>
                <td className="px-4 py-3 border-t border-ink/20 font-semibold">
                  {d.name}
                  {isAutosave(d) && <span className="ml-2 border-2 border-ink bg-secondary px-1.5 py-0.5 text-[9px] uppercase tracking-widest" data-testid={`draft-autosave-tag-${d.id}`}>Autosave</span>}
                </td>
                <td className="px-4 py-3 border-t border-ink/20">{d.period_start} → {d.period_end}</td>
                <td className="px-4 py-3 border-t border-ink/20">{d.pay_date}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right">{d.lines?.length || 0}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-muted-foreground text-xs">{d.updated_at ? new Date(d.updated_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right">
                  <div className="inline-flex items-center gap-2 justify-end">
                    <Link
                      to={`/app/payroll/new?draft=${d.id}`}
                      data-testid={`draft-resume-${d.id}`}
                      className="border-2 border-ink bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-ink hover:text-white inline-flex items-center gap-1"
                    >
                      Resume <ArrowRight className="w-3 h-3" />
                    </Link>
                    <button
                      type="button"
                      data-testid={`draft-manage-delete-${d.id}`}
                      onClick={() => setConfirmIds([d.id])}
                      title="Permanently delete this draft"
                      className="p-1.5 border-2 border-bad text-bad hover:bg-bad hover:text-white"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmIds && (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-6" data-testid="draft-delete-confirm-modal">
          <div className="bg-white border-2 border-ink w-full max-w-lg shadow-hard-lg p-8">
            <div className="overline mb-2">§ PERMANENT ACTION</div>
            <h2 className="font-heading font-black text-3xl mb-3">
              Are you sure you want to permanently delete {confirmIds.length === 1 ? "this payroll draft" : `these ${confirmIds.length} payroll drafts`}?
            </h2>
            <ul className="border-2 border-ink max-h-40 overflow-y-auto mb-4 divide-y divide-ink/20 font-mono text-xs">
              {confirmIds.map((id) => {
                const d = drafts.find((x) => x.id === id);
                return <li key={id} className="px-3 py-2">{d?.name || id}</li>;
              })}
            </ul>
            <div className="border-2 border-good bg-good/10 p-3 mb-6 flex items-start gap-2 font-mono text-xs">
              <ShieldCheck className="w-4 h-4 text-good shrink-0 mt-0.5" />
              <span>Draft records only. Approved timesheets, released pay stubs and payroll history are not affected. This cannot be undone.</span>
            </div>
            {deleteErr && (
              <div className="border-2 border-bad bg-bad/10 text-bad p-3 mb-4 font-mono text-xs" data-testid="draft-delete-error">{deleteErr}</div>
            )}
            <div className="flex items-center justify-between">
              <button onClick={() => { setConfirmIds(null); setDeleteErr(""); }} data-testid="draft-delete-cancel" className="px-6 py-3 border-2 border-ink font-mono uppercase text-xs tracking-widest hover:bg-ink hover:text-white">Cancel</button>
              <button onClick={doDelete} disabled={busy} data-testid="draft-delete-confirm" className="px-6 py-3 border-2 border-bad bg-bad text-white font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
                <Trash2 className="w-4 h-4" /> {busy ? "Deleting…" : "Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
